import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { PlutoConversationHistoryMessage } from "../../hooks/usePlutoConversationHistory";
import type { PlutoOutreachDraft } from "./outreach-draft";
import {
  type PlutoWaitingPrompt,
  type SubmitPromptOptions,
  canEnqueuePlutoPrompt,
  isPlutoBusy,
  shouldOpenPanelOnSubmit,
  shouldReleaseWaitingPrompt,
} from "./pluto-session";

export type { PlutoWaitingPrompt, SubmitPromptOptions };

export type PlutoChatUserMessage = {
  id: string;
  role: "user";
  text: string;
};

/**
 * An answer written as prose, with the tools it was worked out from and any
 * outreach it produced.
 *
 * Drafting is a tool of an ordinary turn, so a draft accompanies the answer
 * rather than replacing it: the prose says which company was written to and on
 * what angle, and the card holds the copy the user edits and sends.
 */
export type PlutoChatAnswerMessage = {
  id: string;
  role: "assistant";
  kind: "text";
  text: string;
  toolNames: readonly string[];
  draft?: PlutoOutreachDraft;
  /** Cut short by the user, so it stops mid-thought rather than concluding. */
  stopped?: boolean;
};

export type PlutoChatMessage = PlutoChatUserMessage | PlutoChatAnswerMessage;

export type PlutoAssistantTurnResult = {
  conversationId: string;
  text: string;
  usedToolNames: readonly string[];
  draft?: PlutoOutreachDraft;
};

/** A stored turn as the transcript shows it. */
export const plutoChatMessageFromStored = (message: PlutoConversationHistoryMessage): PlutoChatMessage => {
  if (message.role === "user") return { id: message.id, role: "user", text: message.text };
  return {
    id: message.id,
    role: "assistant",
    kind: "text",
    text: message.text,
    toolNames: message.toolNames,
    ...(message.draft ? { draft: message.draft } : {}),
    stopped: message.stopped,
  };
};

const createMessageId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `pluto-${String(Date.now())}-${Math.random().toString(16).slice(2)}`;

/**
 * Only the conversation id is kept locally; the transcript itself is reloaded
 * from the server so the two can never drift apart.
 */
const CONVERSATION_STORAGE_KEY = "bloodline.pluto.conversationId";

const readStoredConversationId = (): string | null => {
  try {
    return window.localStorage.getItem(CONVERSATION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredConversationId = (conversationId: string | null) => {
  try {
    if (conversationId === null) window.localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    else window.localStorage.setItem(CONVERSATION_STORAGE_KEY, conversationId);
  } catch {
    // A blocked or full storage must not break the conversation itself.
  }
};

type PlutoContextValue = {
  isOpen: boolean;
  queuedPrompt: string | null;
  queuedPromptSequence: number;
  /** The full transcript of the open conversation, oldest turn first. */
  messages: readonly PlutoChatMessage[];
  conversationId: string | null;
  /** The conversation whose stored transcript is still being restored. */
  pendingHydrationConversationId: string | null;
  isAwaitingReply: boolean;
  /** Whether Pluto is busy, so a new prompt has to wait its turn. */
  isBusy: boolean;
  /** Questions asked while Pluto was busy, oldest first, still to be sent. */
  waitingPrompts: readonly PlutoWaitingPrompt[];
  /** Whether another question can be added to the waiting list. */
  canQueuePrompt: boolean;
  /** Takes a question back off the waiting list before it is ever asked. */
  removeWaitingPrompt: (id: string) => void;
  replyError: string | null;
  openAssistant: () => void;
  closeAssistant: () => void;
  minimizeToPanel: () => void;
  submitPrompt: (prompt: string, options?: SubmitPromptOptions) => void;
  appendUserTurn: (prompt: string) => void;
  completeAssistantTurn: (result: PlutoAssistantTurnResult) => void;
  failPendingTurn: (error: string) => void;
  /** Keeps the unfinished answer of a turn the user stopped. */
  stopPendingTurn: (partialText: string) => void;
  /** Removes a turn the user stopped before the question reached the server. */
  discardPendingTurn: () => void;
  /** Restores a stored transcript; ignored once the user has started talking. */
  hydrateConversation: (messages: readonly PlutoChatMessage[]) => void;
  /** Stops waiting for a transcript that could not be read, keeping the conversation. */
  abandonConversationHydration: () => void;
  openConversation: (conversationId: string) => void;
  startNewConversation: () => void;
};

const PlutoContext = createContext<PlutoContextValue | null>(null);

export const PlutoProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [queuedPrompt, setQueuedPrompt] = useState<string | null>(null);
  const [queuedPromptSequence, setQueuedPromptSequence] = useState(0);
  const [waitingPrompts, setWaitingPrompts] = useState<readonly PlutoWaitingPrompt[]>([]);
  const [messages, setMessages] = useState<readonly PlutoChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(readStoredConversationId);
  const [pendingHydrationConversationId, setPendingHydrationConversationId] = useState<string | null>(
    readStoredConversationId,
  );
  const [isAwaitingReply, setIsAwaitingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  // Whether the answer still being waited for belongs to the open conversation.
  // Leaving a conversation clears it, so a late answer to an abandoned question
  // cannot land in the transcript the user is now reading.
  const hasPendingTurn = useRef(false);
  // The question put on screen ahead of the server's answer, so a turn that
  // never left the browser can be taken back off again.
  const pendingUserMessageId = useRef<string | null>(null);

  const appendUserTurn = useCallback((prompt: string) => {
    hasPendingTurn.current = true;
    setReplyError(null);
    setIsAwaitingReply(true);
    const id = createMessageId();
    pendingUserMessageId.current = id;
    setMessages((current) => [...current, { id, role: "user", text: prompt }]);
  }, []);

  const completeAssistantTurn = useCallback((result: PlutoAssistantTurnResult) => {
    if (!hasPendingTurn.current) return;
    hasPendingTurn.current = false;
    setConversationId(result.conversationId);
    writeStoredConversationId(result.conversationId);
    setIsAwaitingReply(false);
    setReplyError(null);
    setMessages((current) => [
      ...current,
      {
        id: createMessageId(),
        role: "assistant",
        kind: "text",
        text: result.text,
        toolNames: result.usedToolNames,
        ...(result.draft ? { draft: result.draft } : {}),
      },
    ]);
  }, []);

  /**
   * Keeps what Pluto had written when the user stopped it.
   *
   * The turn is marked even when nothing had been written yet, because once the
   * question has reached the server it is stored either way; leaving it out here
   * would make the transcript on screen disagree with the one that comes back on
   * the next load. A stop that beat the request out of the browser is not this
   * case and is discarded instead.
   *
   * No conversation id comes back from a turn that was cut off, so the one
   * already open is left as it is. On a first turn that means the stored
   * conversation is reachable from the history list rather than from here.
   */
  const stopPendingTurn = useCallback((partialText: string) => {
    if (!hasPendingTurn.current) return;
    hasPendingTurn.current = false;
    pendingUserMessageId.current = null;
    setIsAwaitingReply(false);
    setReplyError(null);
    setMessages((current) => [
      ...current,
      {
        id: createMessageId(),
        role: "assistant",
        kind: "text",
        text: partialText,
        toolNames: [],
        stopped: true,
      },
    ]);
  }, []);

  /**
   * Takes back a turn the server never heard about.
   *
   * Stopping while the access token is still being refreshed cuts the request
   * before it is sent, so nothing is stored and nothing was answered. The
   * question goes back off the transcript with it, because leaving it there
   * shows a turn that the next load would not bring back.
   */
  const discardPendingTurn = useCallback(() => {
    if (!hasPendingTurn.current) return;
    hasPendingTurn.current = false;
    setIsAwaitingReply(false);
    setReplyError(null);
    const discarded = pendingUserMessageId.current;
    pendingUserMessageId.current = null;
    if (discarded === null) return;
    setMessages((current) => current.filter((message) => message.id !== discarded));
  }, []);

  const failPendingTurn = useCallback((error: string) => {
    if (!hasPendingTurn.current) return;
    hasPendingTurn.current = false;
    setIsAwaitingReply(false);
    setReplyError(error);
  }, []);

  const hydrateConversation = useCallback((restored: readonly PlutoChatMessage[]) => {
    setPendingHydrationConversationId(null);
    setMessages((current) => (current.length === 0 ? restored : current));
  }, []);

  const abandonConversationHydration = useCallback(() => {
    setPendingHydrationConversationId(null);
  }, []);

  const openConversation = useCallback((nextConversationId: string) => {
    hasPendingTurn.current = false;
    // The transcript is left empty on purpose: it is restored from the server,
    // which is what makes the reopened conversation include its older turns.
    setMessages([]);
    setConversationId(nextConversationId);
    setPendingHydrationConversationId(nextConversationId);
    writeStoredConversationId(nextConversationId);
    setQueuedPrompt(null);
    setIsAwaitingReply(false);
    setReplyError(null);
  }, []);

  const startNewConversation = useCallback(() => {
    hasPendingTurn.current = false;
    setMessages([]);
    setConversationId(null);
    setPendingHydrationConversationId(null);
    writeStoredConversationId(null);
    setQueuedPrompt(null);
    setIsAwaitingReply(false);
    setReplyError(null);
  }, []);

  const isBusy = isPlutoBusy({ isAwaitingReply, pendingHydrationConversationId });

  const sendPrompt = useCallback((prompt: string) => {
    setQueuedPrompt(prompt);
    setQueuedPromptSequence((sequence) => sequence + 1);
  }, []);

  // A question that waited is sent the moment Pluto is free, so the reader gets
  // the follow-up they already asked for without asking again.
  useEffect(() => {
    if (!shouldReleaseWaitingPrompt({ isBusy, replyError, waitingPrompts })) return;
    const [next, ...rest] = waitingPrompts;
    if (next === undefined) return;
    setWaitingPrompts(rest);
    sendPrompt(next.text);
  }, [isBusy, replyError, sendPrompt, waitingPrompts]);

  const removeWaitingPrompt = useCallback((id: string) => {
    setWaitingPrompts((current) => current.filter((prompt) => prompt.id !== id));
  }, []);

  const value = useMemo<PlutoContextValue>(
    () => ({
      isOpen,
      queuedPrompt,
      queuedPromptSequence,
      messages,
      conversationId,
      pendingHydrationConversationId,
      isAwaitingReply,
      isBusy,
      waitingPrompts,
      canQueuePrompt: canEnqueuePlutoPrompt(waitingPrompts),
      removeWaitingPrompt,
      replyError,
      openAssistant: () => setIsOpen(true),
      closeAssistant: () => setIsOpen(false),
      minimizeToPanel: () => setIsOpen(true),
      appendUserTurn,
      completeAssistantTurn,
      failPendingTurn,
      stopPendingTurn,
      discardPendingTurn,
      hydrateConversation,
      abandonConversationHydration,
      openConversation,
      startNewConversation,
      submitPrompt: (prompt, options) => {
        const normalizedPrompt = prompt.trim();
        if (!normalizedPrompt) return;
        if (shouldOpenPanelOnSubmit(options)) setIsOpen(true);
        // One question at a time on the wire: a second turn started before the
        // first answer arrives would be built from the same stale history, and
        // one started before the stored transcript is back would replace it. So
        // a question asked meanwhile waits rather than being lost, which is what
        // used to happen to it.
        if (isBusy) {
          setWaitingPrompts((current) =>
            canEnqueuePlutoPrompt(current) ? [...current, { id: createMessageId(), text: normalizedPrompt }] : current,
          );
          return;
        }
        sendPrompt(normalizedPrompt);
      },
    }),
    [
      abandonConversationHydration,
      appendUserTurn,
      completeAssistantTurn,
      conversationId,
      discardPendingTurn,
      failPendingTurn,
      stopPendingTurn,
      hydrateConversation,
      isAwaitingReply,
      isBusy,
      isOpen,
      messages,
      removeWaitingPrompt,
      sendPrompt,
      waitingPrompts,
      openConversation,
      pendingHydrationConversationId,
      queuedPrompt,
      queuedPromptSequence,
      replyError,
      startNewConversation,
    ],
  );

  return <PlutoContext.Provider value={value}>{children}</PlutoContext.Provider>;
};

export const usePluto = () => {
  const context = useContext(PlutoContext);
  if (!context) throw new Error("usePluto must be used within PlutoProvider");
  return context;
};
